"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { Building2, ExternalLink, Search, Star, X } from "lucide-react";
import type { GpwCompanyDirectoryEntry } from "@/lib/gpw-companies/types";

const WATCHLIST_KEY = "rynkowefakty:gpw-company-watchlist:v1";

function normalize(value: string) {
  return value
    .toLocaleLowerCase("pl")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function CompanyRow({
  company,
  watched,
  onToggle,
}: {
  company: GpwCompanyDirectoryEntry;
  watched: boolean;
  onToggle: (isin: string) => void;
}) {
  const leadIndex = ["WIG20", "mWIG40", "sWIG80"].find((index) =>
    company.indexes.includes(index),
  );

  return (
    <li className="grid min-h-20 gap-3 border-b border-white/10 py-4 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded border border-[#3498db]/35 bg-[#3498db]/10 px-2 py-0.5 font-mono text-xs font-semibold text-[#76c5f5]">
            {company.ticker}
          </span>
          {leadIndex ? (
            <span className="rounded border border-[#FFD700]/25 bg-[#FFD700]/10 px-2 py-0.5 text-xs font-medium text-[#FFD700]">
              {leadIndex}
            </span>
          ) : null}
        </div>
        <Link
          className="mt-2 block break-words font-semibold text-white transition hover:text-[#76c5f5]"
          href={`/spolki/${company.slug}`}
        >
          {company.name}
        </Link>
        <p className="mt-1 text-sm text-slate-400">
          {company.sector} · {company.isin}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button
          aria-label={watched ? `Usuń ${company.name} z obserwowanych` : `Obserwuj ${company.name}`}
          aria-pressed={watched}
          className={`inline-flex size-10 shrink-0 items-center justify-center rounded-md border transition ${
            watched
              ? "border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700]"
              : "border-white/15 bg-white/[0.035] text-slate-300 hover:border-[#FFD700]/45 hover:text-[#FFD700]"
          }`}
          onClick={() => onToggle(company.isin)}
          title={watched ? "Usuń z obserwowanych" : "Dodaj do obserwowanych"}
          type="button"
        >
          <Star aria-hidden="true" className={`size-4 ${watched ? "fill-current" : ""}`} />
        </button>
        <a
          aria-label={`Profil ${company.name} w serwisie GPW`}
          className="inline-flex size-10 shrink-0 items-center justify-center rounded-md border border-white/15 bg-white/[0.035] text-slate-300 transition hover:border-[#3498db]/55 hover:text-white"
          href={company.sourceUrl}
          rel="noreferrer"
          target="_blank"
          title="Oficjalny profil GPW"
        >
          <ExternalLink aria-hidden="true" className="size-4" />
        </a>
      </div>
    </li>
  );
}

export function CompaniesHubClient({ companies }: { companies: GpwCompanyDirectoryEntry[] }) {
  const [query, setQuery] = useState("");
  const [sector, setSector] = useState("all");
  const [letter, setLetter] = useState("all");
  const [watchedIsins, setWatchedIsins] = useState<string[]>([]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      try {
        const saved = JSON.parse(localStorage.getItem(WATCHLIST_KEY) ?? "[]") as unknown;
        if (Array.isArray(saved)) {
          setWatchedIsins(saved.filter((item): item is string => typeof item === "string"));
        }
      } catch {
        setWatchedIsins([]);
      }
    }, 0);

    return () => window.clearTimeout(timeout);
  }, []);

  const sectors = useMemo(
    () =>
      [...new Set(companies.map((company) => company.sector))].sort((left, right) =>
        left.localeCompare(right, "pl"),
      ),
    [companies],
  );

  const letters = useMemo(
    () =>
      [...new Set(companies.map((company) => company.name.charAt(0).toLocaleUpperCase("pl")))].sort(
        (left, right) => left.localeCompare(right, "pl"),
      ),
    [companies],
  );

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = normalize(query.trim());
    return companies.filter((company) => {
      const matchesQuery =
        !normalizedQuery ||
        normalize(`${company.name} ${company.ticker} ${company.isin} ${company.sector}`).includes(
          normalizedQuery,
        );
      const matchesSector = sector === "all" || company.sector === sector;
      const matchesLetter = letter === "all" || company.name.charAt(0).toLocaleUpperCase("pl") === letter;
      return matchesQuery && matchesSector && matchesLetter;
    });
  }, [companies, letter, query, sector]);

  const queryMatchesCatalog = useMemo(() => {
    const normalizedQuery = normalize(query.trim());
    return !normalizedQuery || companies.some((company) =>
      normalize(`${company.name} ${company.ticker} ${company.isin} ${company.sector}`).includes(normalizedQuery),
    );
  }, [companies, query]);

  const watchedCompanies = useMemo(
    () => companies.filter((company) => watchedIsins.includes(company.isin)),
    [companies, watchedIsins],
  );

  function toggleWatched(isin: string) {
    setWatchedIsins((current) => {
      const next = current.includes(isin)
        ? current.filter((item) => item !== isin)
        : [...current, isin];
      localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next));
      return next;
    });
  }

  function clearFilters() {
    setQuery("");
    setSector("all");
    setLetter("all");
  }

  return (
    <>
      <section className="scroll-mt-24 border-y border-white/10 bg-[#101722]/78 px-4 py-12 sm:px-6 lg:px-8" id="wyszukiwarka">
        <div className="mx-auto max-w-6xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FFD700]">Katalog GPW</p>
          <h2 className="mt-3 text-3xl font-semibold text-white">Wyszukaj spółkę</h2>
          <div className="mt-7 grid gap-3 lg:grid-cols-[minmax(0,1fr)_280px_auto]">
            <label className="relative block">
              <span className="sr-only">Nazwa, ticker, ISIN lub sektor</span>
              <Search aria-hidden="true" className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-slate-400" />
              <input
                className="h-12 w-full rounded-md border border-white/15 bg-[#0e1117] pl-12 pr-4 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-[#3498db] focus:ring-2 focus:ring-[#3498db]/20"
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Nazwa, ticker, ISIN lub sektor"
                type="search"
                value={query}
              />
            </label>
            <label>
              <span className="sr-only">Filtr sektora</span>
              <select
                className="h-12 w-full rounded-md border border-white/15 bg-[#0e1117] px-4 text-sm text-white outline-none transition focus:border-[#3498db] focus:ring-2 focus:ring-[#3498db]/20"
                onChange={(event) => setSector(event.target.value)}
                value={sector}
              >
                <option value="all">Wszystkie sektory</option>
                {sectors.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </label>
            <button
              className="inline-flex h-12 items-center justify-center gap-2 rounded-md border border-white/15 px-4 text-sm font-semibold text-slate-200 transition hover:border-[#3498db]/55 hover:text-white"
              onClick={clearFilters}
              type="button"
            >
              <X aria-hidden="true" className="size-4" />
              Wyczyść
            </button>
          </div>
          <p aria-live="polite" className="mt-4 text-sm text-slate-400">
            Wyniki: <span className="font-semibold text-white">{filteredCompanies.length}</span> z {companies.length} spółek
          </p>
        </div>
      </section>

      <section className="scroll-mt-24 px-4 py-14 sm:px-6 lg:px-8" id="obserwowane">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FFD700]">Początek produktu</p>
            <h2 className="mt-3 text-3xl font-semibold text-white">Najczęściej obserwowane</h2>
            <p className="mt-4 leading-7 text-slate-300">
              Publiczny ranking pojawi się dopiero po zebraniu wystarczającej, anonimowej próby. Nie pokazujemy fikcyjnej popularności. Na razie możesz tworzyć własną listę obserwowanych, zapisywaną wyłącznie w tej przeglądarce.
            </p>
          </div>
          {watchedCompanies.length ? (
            <div className="mt-8 rounded-lg border border-[#FFD700]/20 bg-[#FFD700]/[0.04] px-5">
              <h3 className="border-b border-white/10 py-4 text-lg font-semibold text-white">Twoje obserwowane spółki</h3>
              <ul>
                {watchedCompanies.map((company) => (
                  <CompanyRow company={company} key={company.isin} onToggle={toggleWatched} watched />
                ))}
              </ul>
            </div>
          ) : (
            <div className="mt-8 flex items-start gap-4 rounded-lg border border-white/10 bg-white/[0.035] p-5">
              <Star aria-hidden="true" className="mt-1 size-5 shrink-0 text-[#FFD700]" />
              <p className="text-sm leading-7 text-slate-300">
                Twoja lista jest pusta. Użyj ikony gwiazdki przy spółce w indeksie A–Z. Dane nie opuszczają przeglądarki.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="scroll-mt-24 bg-white/[0.025] px-4 py-14 sm:px-6 lg:px-8" id="lista-spolek">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FFD700]">Indeks SEO i katalog</p>
              <h2 className="mt-3 text-3xl font-semibold text-white">Wszystkie spółki GPW alfabetycznie</h2>
            </div>
            <p className="text-sm text-slate-400">Źródło nazw, tickerów i ISIN: oficjalna lista GPW</p>
          </div>

          <div aria-label="Filtr alfabetyczny" className="mt-8 flex flex-wrap gap-2">
            <button
              className={`min-h-9 rounded-md border px-3 text-sm font-semibold transition ${letter === "all" ? "border-[#3498db] bg-[#3498db] text-[#07111d]" : "border-white/15 text-slate-300 hover:border-[#3498db]/55 hover:text-white"}`}
              onClick={() => setLetter("all")}
              type="button"
            >
              A–Z
            </button>
            {letters.map((item) => (
              <button
                className={`size-9 rounded-md border text-sm font-semibold transition ${letter === item ? "border-[#3498db] bg-[#3498db] text-[#07111d]" : "border-white/15 text-slate-300 hover:border-[#3498db]/55 hover:text-white"}`}
                key={item}
                onClick={() => setLetter(item)}
                type="button"
              >
                {item}
              </button>
            ))}
          </div>

          {filteredCompanies.length ? (
            <ul className="mt-8 grid gap-x-8 rounded-lg border border-white/10 bg-[#0e1117]/70 px-5 lg:grid-cols-2">
              {filteredCompanies.map((company) => (
                <CompanyRow
                  company={company}
                  key={company.isin}
                  onToggle={toggleWatched}
                  watched={watchedIsins.includes(company.isin)}
                />
              ))}
            </ul>
          ) : (
            <div className="mt-8 flex min-h-44 items-center justify-center rounded-lg border border-dashed border-white/15 bg-[#0e1117]/70 p-6 text-center">
              <div>
                <Building2 aria-hidden="true" className="mx-auto size-7 text-slate-500" />
                <p className="mt-4 break-words font-semibold text-white">
                  {queryMatchesCatalog ? "Brak spółek dla wybranych filtrów" : `Nie mamy jeszcze wystarczających danych dla „${query.trim()}”, aby pokazać profil spółki.`}
                </p>
                {!queryMatchesCatalog ? <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">Brak wyniku w naszym katalogu nie oznacza, że spółka nie istnieje na GPW. Sprawdź nazwę, ticker lub ISIN; nie uzupełniamy braków zgadywaniem.</p> : null}
                <button className="mt-3 text-sm font-semibold text-[#54afe9] hover:text-white" onClick={clearFilters} type="button">
                  Wyczyść filtry
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
